/**
 * Axios interceptor for handling 401 errors and token refresh.
 * 
 * When a 401 error is received:
 * 1. Attempts to refresh the auth token
 * 2. If refresh succeeds, retries the original request
 * 3. If refresh fails with invalid_grant, triggers force logout
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getCurrentAuthToken } from './Auth';
import { dispatchForceLogout } from './Auth';
import logger from './Logger';

// Track if we're already refreshing to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function subscribeTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
}

function onTokenRefreshFailed() {
  refreshSubscribers = [];
}

interface RetryableRequest extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

/**
 * Sets up axios interceptors to handle 401 errors globally.
 * Call this once during app initialization.
 */
export function setupAxiosInterceptors() {
  // Response interceptor to handle 401 errors
  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as RetryableRequest | undefined;
      
      // Only handle 401 errors
      if (error.response?.status !== 401 || !originalRequest) {
        return Promise.reject(error);
      }

      // Prevent infinite retry loops
      if (originalRequest._retry) {
        logger.warn('[Axios] Already retried request, not retrying again');
        return Promise.reject(error);
      }

      // Check if the error response contains invalid_grant
      const responseData = error.response?.data as { error?: string } | undefined;
      if (responseData?.error === 'invalid_grant') {
        logger.error('[Axios] Received invalid_grant error - forcing logout');
        dispatchForceLogout('invalid_grant: API returned 401 with invalid_grant');
        return Promise.reject(error);
      }

      // Mark this request as being retried
      originalRequest._retry = true;

      if (isRefreshing) {
        // If already refreshing, wait for the refresh to complete
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((token: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            resolve(axios(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        logger.info('[Axios] 401 received, attempting token refresh');
        
        // Attempt to get a fresh token (this will trigger signinSilent if expired)
        const newToken = await getCurrentAuthToken();
        
        if (!newToken) {
          logger.warn('[Axios] Token refresh returned no token - session may be invalid');
          onTokenRefreshFailed();
          isRefreshing = false;
          // Don't dispatch force logout here - getCurrentAuthToken already handles that
          return Promise.reject(error);
        }

        logger.info('[Axios] Token refreshed successfully, retrying request');
        
        // Update the Authorization header
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        
        onTokenRefreshed(newToken);
        isRefreshing = false;
        
        return axios(originalRequest);
      } catch (refreshError: any) {
        logger.error('[Axios] Token refresh failed', refreshError);
        
        // Check for invalid_grant or 401 in the refresh error
        const errorMessage = refreshError?.message || refreshError?.error || String(refreshError);
        const status = refreshError?.status || refreshError?.response?.status;
        const isInvalidGrant = errorMessage.includes('invalid_grant') || refreshError?.error === 'invalid_grant';
        const is401 = status === 401 || errorMessage.includes('401');
        
        if (isInvalidGrant || is401) {
          logger.error(`[Axios] Token refresh failed (invalid_grant: ${isInvalidGrant}, 401: ${is401}) - forcing logout`);
          dispatchForceLogout(`Token refresh failed: ${isInvalidGrant ? 'invalid_grant' : '401'}`);
        }
        
        onTokenRefreshFailed();
        isRefreshing = false;
        return Promise.reject(refreshError);
      }
    }
  );

  logger.info('[Axios] Interceptors configured for 401 handling');
}

